/**
 * ゲームAPI クライアント
 *
 * Backend REST API との通信
 */

import axios, { type AxiosInstance } from 'axios';
import type { GameConfig, GameState, Action } from '@/types/game';
import type { CreateGameResponse, ActionResponse, HealthResponse } from '@/types/api';

/**
 * Axios インスタンス作成
 */
const createApiClient = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

  return axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const apiClient = createApiClient();

/**
 * ゲームAPIクライアント
 */
export const gameApi = {
  /**
   * ヘルスチェック
   *
   * @returns {Promise<HealthResponse>} ヘルスチェック結果
   */
  async healthCheck(): Promise<HealthResponse> {
    const response = await apiClient.get<HealthResponse>('/health');
    return response.data;
  },

  /**
   * 新規ゲーム作成
   *
   * @param config ゲーム作成設定
   * @returns {Promise<CreateGameResponse>} 作成されたゲーム情報
   */
  async createGame(config: GameConfig): Promise<CreateGameResponse> {
    const response = await apiClient.post<CreateGameResponse>('/games', config);
    return response.data;
  },

  /**
   * ゲーム状態取得
   *
   * @param gameId ゲームID
   * @returns {Promise<GameState>} ゲーム状態
   */
  async getGame(gameId: string): Promise<GameState> {
    const response = await apiClient.get<GameState>(`/games/${gameId}`);
    return response.data;
  },

  /**
   * アクション実行
   *
   * @param gameId ゲームID
   * @param action アクション情報
   * @returns {Promise<ActionResponse>} アクション実行結果
   */
  async executeAction(gameId: string, action: Action): Promise<ActionResponse> {
    const response = await apiClient.post<ActionResponse>(`/games/${gameId}/actions`, action);
    return response.data;
  },

  /**
   * ゲーム削除
   *
   * @param gameId ゲームID
   */
  async deleteGame(gameId: string): Promise<void> {
    await apiClient.delete(`/games/${gameId}`);
  },
};

/**
 * APIエラーハンドリング用ヘルパー
 */
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || error.message || 'API通信エラー';
  }
  return 'Unknown error occurred';
};
